import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import TeethSelector from './TeethSelector';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import { apiClient } from '../../services/apiClient';
import jsPDF from 'jspdf';
import medidentNeBg1 from '../../assets/medident-neuchatel-fr-1.png';
import medidentNeBg2 from '../../assets/medident-neuchatel-fr-2.png';
import colors from '../../config/colors';
import { normalizeTeethListFields, parseTeethList } from '../../utils/teethList';
import EuropeanDateInput from '../common/EuropeanDateInput';

export const medidentNeDefaultFormData = {
  'office-aide-sociale': false,
  'office-caisse-compensation': false,
  'office-asile': false,
  'office-csp': false,
  'office-caritas': false,
  'office-pro-senectute': false,
  'office-pro-infirmis': false,
  'patient-name': '',
  'patient-firstname': '',
  'patient-address': '',
  'patient-phone': '',
  'patient-email': '',
  'patient-birth': '',
  'patient-sex': '',
  'patient-avs': '',
  'patient-asylum-n': '',
  'patient-personnel-nr': '',
  'dentist-office': '',
  'dentist': '',
  'rcc-nr': '',
  'phone-nr': '',
  'employee-name': '',
  'is-lamal': 'non',
  'is-laa': 'non',
  'is-pain-only': 'non',
  'first-consultation': '',
  'missing-teeth': '',
  'defective-teeth': '',
  'repaired-teeth': '',
  'periodontal-teeth': '',
  'prosthetic-description': '',
  'hygiene': '',
  'habits': '',
  'motivation': '',
  'diagnostic': '',
  'emergency-treatment': '',
  'priority2-treatment': '',
  'dental-schema-teeth': '',
  'comments': '',
  'place-date': '',
};

const OFFICE_FIELDS = [
  { key: 'office-aide-sociale', labelKey: 'officeAideSociale' },
  { key: 'office-caisse-compensation', labelKey: 'officeCaisseCompensation' },
  { key: 'office-asile', labelKey: 'officeAsile' },
  { key: 'office-csp', labelKey: 'officeCsp' },
  { key: 'office-caritas', labelKey: 'officeCaritas' },
  { key: 'office-pro-senectute', labelKey: 'officeProSenectute' },
  { key: 'office-pro-infirmis', labelKey: 'officeProInfirmis' },
];

const HYGIENE_OPTIONS = ['Très bonne', 'Bonne', 'Moyenne', 'Mauvaise', 'Très mauvaise', 'Non documentée'];
const MOTIVATION_OPTIONS = ['Très motivé', 'Motivé', 'Peu motivé', 'Non motivé', 'Non documentée'];

function debounce(fn, delay) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

const wrapText = (doc, text, x, y, maxWidth, lineHeight, fontSize) => {
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(String(text || ''), maxWidth);
  lines.forEach((line, i) => {
    doc.text(line, x, y + i * lineHeight);
  });
  return y + lines.length * lineHeight;
};

/**
 * Multi-column text layout.
 * Picks the fewest columns (1→2→3) such that after wrapping at the real column width,
 * no column exceeds maxRowsPerCol lines (panel vertical capacity).
 * - byEntries: distribute newline-separated entries, then wrap each entry in its column.
 */
const wrapTextMultiColumn = (
  doc,
  text,
  x,
  y,
  maxWidth,
  lineHeight,
  fontSize,
  { gap = 12, maxRowsPerCol = 5, byEntries = false } = {}
) => {
  if (!text) return y;
  doc.setFontSize(fontSize);

  const entries = byEntries
    ? String(text).split(/\r?\n/).map((s) => s.trim()).filter(Boolean)
    : null;

  const colWidthFor = (cols) => (maxWidth - gap * (cols - 1)) / cols;

  const maxLinesIfCols = (cols) => {
    const colWidth = colWidthFor(cols);
    if (byEntries) {
      const perCol = Math.ceil(entries.length / cols);
      let worst = 0;
      for (let c = 0; c < cols; c++) {
        const colEntries = entries.slice(c * perCol, (c + 1) * perCol);
        const lineCount = colEntries.reduce(
          (sum, entry) => sum + doc.splitTextToSize(entry, colWidth).length,
          0
        );
        worst = Math.max(worst, lineCount);
      }
      return worst;
    }
    const lines = doc.splitTextToSize(String(text), colWidth);
    return Math.ceil(lines.length / cols);
  };

  let cols = 1;
  if (maxLinesIfCols(1) > maxRowsPerCol) {
    cols = maxLinesIfCols(2) <= maxRowsPerCol ? 2 : 3;
  }

  if (cols === 1) {
    return wrapText(doc, text, x, y, maxWidth, lineHeight, fontSize);
  }

  const colWidth = colWidthFor(cols);

  if (byEntries) {
    const perCol = Math.ceil(entries.length / cols);
    let maxLines = 0;
    for (let c = 0; c < cols; c++) {
      const colEntries = entries.slice(c * perCol, (c + 1) * perCol);
      const colX = x + c * (colWidth + gap);
      let lineOffset = 0;
      colEntries.forEach((entry) => {
        doc.splitTextToSize(entry, colWidth).forEach((line) => {
          doc.text(line, colX, y + lineOffset * lineHeight);
          lineOffset += 1;
        });
      });
      maxLines = Math.max(maxLines, lineOffset);
    }
    return y + maxLines * lineHeight;
  }

  const lines = doc.splitTextToSize(String(text), colWidth);
  const perCol = Math.ceil(lines.length / cols);
  for (let c = 0; c < cols; c++) {
    const colLines = lines.slice(c * perCol, (c + 1) * perCol);
    const colX = x + c * (colWidth + gap);
    colLines.forEach((line, i) => {
      doc.text(line, colX, y + i * lineHeight);
    });
  }
  return y + perCol * lineHeight;
};

// Background PNG native size; all layout coords below are in image pixels.
const MEDIDENT_IMG_W = 1191;
const MEDIDENT_IMG_H = 1684;

const isUpperTooth = (tooth) => {
  const n = Number(tooth);
  return (n >= 11 && n <= 28) || (n >= 51 && n <= 68);
};

// Tooth number centers on section 3.x grids (image px)
const toothGridX = {
  55: 386.4, 54: 406.55, 53: 426.65, 52: 446.35, 51: 466.25,
  61: 528.75, 62: 548.55, 63: 568.6, 64: 588.5, 65: 608.1,
  18: 709.5, 17: 733.5, 16: 756.45, 15: 780.2, 14: 803.65, 13: 827, 12: 850.45, 11: 878,
  21: 945, 22: 960.2, 23: 980.15, 24: 999.75, 25: 1019.55, 26: 1039.3, 27: 1059.6, 28: 1079.1,
  85: 386.4, 84: 406.55, 83: 426.65, 82: 446.35, 81: 466.25,
  71: 528.75, 72: 548.55, 73: 568.6, 74: 588.5, 75: 608.1,
  48: 709.5, 47: 733.5, 46: 756.45, 45: 780.2, 44: 803.65, 43: 827, 42: 850.45, 41: 878,
  31: 945, 32: 960.2, 33: 980.15, 34: 999.75, 35: 1019.55, 36: 1039.3, 37: 1059.6, 38: 1079.1,
};

const teethGridY = {
  'missing-teeth': { upper: 666, lower: 685 },
  'defective-teeth': { upper: 720, lower: 738 },
  'repaired-teeth': { upper: 773, lower: 791 },
  'periodontal-teeth': { upper: 826, lower: 844 },
};

// Page 2 schéma dentaire — occlusal arches (image px)
// Page 2 §5.3 schéma dentaire — centers of occlusal tooth drawings (image px)
const schemaToothPositions = {
  18: [256, 394], 17: [246, 374], 16: [241, 351], 15: [246, 329], 14: [257, 309],
  13: [276, 291], 12: [299, 269], 11: [326, 263],
  21: [354, 263], 22: [381, 269], 23: [404, 291], 24: [423, 309], 25: [435, 329],
  26: [439, 351], 27: [435, 374], 28: [424, 394],
  48: [807, 291], 47: [802, 316], 46: [807, 341], 45: [817, 362], 44: [834, 382],
  43: [853, 397], 42: [875, 411], 41: [896, 422],
  31: [917, 422], 32: [938, 411], 33: [957, 397], 34: [972, 382], 35: [983, 362],
  36: [989, 341], 37: [991, 316], 38: [989, 291],
};

const officeMarksImg = [
  { key: 'office-aide-sociale', x: 151.5, y: 129.5 },
  { key: 'office-caisse-compensation', x: 151.5, y: 151.5 },
  { key: 'office-asile', x: 151.5, y: 174.5 },
  { key: 'office-csp', x: 700.5, y: 118 },
  { key: 'office-caritas', x: 700.5, y: 140.5 },
  { key: 'office-pro-senectute', x: 700.5, y: 163 },
  { key: 'office-pro-infirmis', x: 700.5, y: 185.5 },
];

const MedidentNeForm = ({ initialData, onSave }) => {
  const { t } = useTranslation('common');
  const [formData, setFormData] = useState(() => ({
    ...medidentNeDefaultFormData,
    ...normalizeTeethListFields(initialData || {}),
  }));

  useEffect(() => {
    setFormData({
      ...medidentNeDefaultFormData,
      ...normalizeTeethListFields(initialData || {}),
    });
  }, [initialData]);

  const debouncedSave = useRef(
    debounce((data) => {
      if (typeof onSave === 'function') {
        onSave(data);
      }
    }, 500)
  ).current;

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      debouncedSave(next);
      return next;
    });
  };

  const isFieldEmpty = (field) => {
    const value = formData[field];
    if (typeof value === 'boolean') {
      return !value;
    }
    return value == null || String(value).trim() === '';
  };

  const isSectionComplete = ({ sectionFields = [], requireAtLeastOne = false, isOptional = false }) => {
    if (!sectionFields.length) {
      return false;
    }
    if (requireAtLeastOne) {
      return sectionFields.some((field) => !isFieldEmpty(field));
    }
    if (isOptional) {
      return sectionFields.some((field) => !isFieldEmpty(field));
    }
    return sectionFields.every((field) => !isFieldEmpty(field));
  };

  const renderAccordionSection = ({
    title,
    children,
    sectionFields = [],
    isOptional = false,
    requireAtLeastOne = false,
  }) => {
    const hasEmptyMandatory = !isOptional && (
      requireAtLeastOne
        ? !sectionFields.some((field) => !isFieldEmpty(field))
        : sectionFields.some((field) => isFieldEmpty(field))
    );
    const showFilled = isSectionComplete({ sectionFields, requireAtLeastOne, isOptional });

    return (
      <Disclosure>
        {({ open }) => (
          <div className={`mb-4 rounded-lg ${open ? 'bg-white shadow-md' : ''}`}>
            <Disclosure.Button
              className="flex w-full justify-between rounded-lg px-4 py-2 text-left text-sm font-medium
                bg-white text-gray-900 shadow-sm hover:bg-gray-50 focus:outline-none
                focus-visible:ring focus-visible:ring-blue-500 focus-visible:ring-opacity-50"
            >
              <span>{title}</span>
              <div className="flex items-center gap-2">
                {!isOptional && hasEmptyMandatory ? (
                  <span className="px-2 py-1 text-xs font-medium bg-red-50 text-red-600 rounded">
                    {t('insurance.labels.required')}
                  </span>
                ) : showFilled ? (
                  <span className="px-2 py-1 text-xs font-medium bg-green-50 text-green-600 rounded">
                    {t('insurance.labels.filled')}
                  </span>
                ) : null}
                <ChevronUpIcon className={`${open ? 'rotate-180 transform' : ''} h-5 w-5 text-gray-500 ml-2`} />
              </div>
            </Disclosure.Button>
            <Disclosure.Panel className="px-4 pt-4 pb-2 text-sm text-gray-600">
              {children}
            </Disclosure.Panel>
          </div>
        )}
      </Disclosure>
    );
  };

  const renderField = (field, label, type = 'text', required = false) => (
    <div className="flex flex-col space-y-1">
      {label && (
        <label className="text-sm font-medium text-gray-700 flex items-center gap-2">
          {label}
          {required && <span className="text-red-500">*</span>}
        </label>
      )}
      {type === 'textarea' ? (
        <textarea
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          rows={4}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 mt-1 block w-full"
        />
      ) : type === 'select' ? null : type === 'date' ? (
        <EuropeanDateInput
          value={formData[field] || ''}
          onChange={(value) => handleChange(field, value)}
          required={required}
        />
      ) : (
        <input
          type={type}
          value={formData[field] || ''}
          onChange={(e) => handleChange(field, e.target.value)}
          className="rounded-md shadow-sm border-gray-300 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
        />
      )}
    </div>
  );

  const renderOuiNon = (field, label, required = false) => (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
      <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
        {label}
        {required && <span className="text-red-500">*</span>}
      </span>
      <div className="flex items-center gap-4">
        {['oui', 'non'].map((opt) => (
          <label key={opt} className="inline-flex items-center gap-1 text-sm cursor-pointer">
            <input
              type="radio"
              name={field}
              checked={formData[field] === opt}
              onChange={() => handleChange(field, opt)}
              className="h-4 w-4 border-gray-300 text-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)]"
            />
            {opt}
          </label>
        ))}
      </div>
    </div>
  );

  const drawMarkX = (doc, ix, iy, fontSize = 10) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;
    doc.setFontSize(fontSize);
    doc.setTextColor(0, 0, 0);
    doc.text('X', ix * sx, iy * sy, { align: 'center', baseline: 'middle' });
  };

  // Green square for §5.3 schéma dentaire (distinct from black X on number grids)
  const drawSchemaToothMark = (doc, ix, iy, halfSizeImg = 5) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;
    const w = halfSizeImg * 2 * sx;
    const h = halfSizeImg * 2 * sy;
    doc.setFillColor(34, 197, 94);
    doc.rect(ix * sx - w / 2, iy * sy - h / 2, w, h, 'F');
  };

  const drawImgText = (doc, text, ix, iy, maxWidthImg, lineHeightImg, fontSize) => {
    if (!text) return;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;
    // +2 image-px between lines for multiline fields
    wrapText(doc, text, ix * sx, iy * sy, maxWidthImg * sx, (lineHeightImg + 2) * sy, fontSize);
  };

  /** Multi-column PDF text (diagnostic §4, emergency §5.1, …). */
  const drawImgTextMultiColumn = (doc, text, ix, iy, maxWidthImg, lineHeightImg, fontSize, options = {}) => {
    if (!text) return;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;
    wrapTextMultiColumn(
      doc,
      text,
      ix * sx,
      iy * sy,
      maxWidthImg * sx,
      (lineHeightImg + 2) * sy,
      fontSize,
      { gap: 16 * sx, ...options }
    );
  };

  const drawTeethOnGrid = (doc, field) => {
    const row = teethGridY[field];
    if (!row) return;
    const teeth = parseTeethList(formData[field]);
    teeth.forEach((tooth) => {
      const x = toothGridX[tooth];
      if (x == null) return;
      const y = isUpperTooth(tooth) ? row.upper : row.lower;
      drawMarkX(doc, x, y, 8);
    });
  };

  const handleGeneratePDF = async () => {
    if (typeof onSave === 'function') {
      onSave(formData);
    }

    const doc = new jsPDF({ unit: 'px', format: 'a4' });
    const getImageBase64 = (src) =>
      new Promise((resolve) => {
        const img = new window.Image();
        img.crossOrigin = 'Anonymous';
        img.onload = function () {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.src = src;
      });

    const [bg1, bg2] = await Promise.all([
      getImageBase64(medidentNeBg1),
      getImageBase64(medidentNeBg2),
    ]);

    let signatureDataUrl = null;
    let signatureType = 'JPEG';
    try {
      const profile = await apiClient.get('/api/profile');
      if (profile?.signature_image) {
        signatureDataUrl = profile.signature_image;
        signatureType = signatureDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      }
    } catch (e) {
      // ignore
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const sx = pageWidth / MEDIDENT_IMG_W;
    const sy = pageHeight / MEDIDENT_IMG_H;

    // Page 1
    doc.addImage(bg1, 'PNG', 0, 0, pageWidth, pageHeight);

    officeMarksImg.forEach(({ key, x, y }) => {
      if (formData[key]) {
        drawMarkX(doc, x, y, 11);
      }
    });

    const patientFields = [
      ['patient-name', 200, 258],
      ['patient-firstname', 215, 277],
      ['patient-address', 215, 297],
      ['patient-phone', 235, 316],
      ['patient-email', 200, 336],
      ['patient-birth', 290, 355],
      ['patient-avs', 210, 392],
      ['patient-asylum-n', 330, 432],
      ['patient-personnel-nr', 310, 453],
    ];
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    patientFields.forEach(([key, ix, iy]) => {
      if (formData[key]) {
        doc.text(String(formData[key]), ix * sx, iy * sy);
      }
    });

    if (formData['patient-sex'] === 'm') {
      drawMarkX(doc, 217.5, 374, 10);
    } else if (formData['patient-sex'] === 'f') {
      drawMarkX(doc, 278.5, 374, 10);
    }

    drawImgText(doc, formData['dentist-office'], 700, 255, 400, 12, 7);
    if (formData.dentist) {
      doc.setFontSize(8);
      doc.text(String(formData.dentist), 700 * sx, 455 * sy);
    }
    if (formData['rcc-nr']) {
      doc.setFontSize(8);
      doc.text(String(formData['rcc-nr']), 900 * sx, 455 * sy);
    }
    if (formData['phone-nr']) {
      doc.setFontSize(8);
      doc.text(String(formData['phone-nr']), 700 * sx, 468 * sy);
    }
    if (formData['employee-name']) {
      doc.setFontSize(8);
      doc.text(String(formData['employee-name']), 700 * sx, 481 * sy);
    }

    const markOui = (value, ouiX, nonX, y) => {
      drawMarkX(doc, String(value).toLowerCase() === 'oui' ? ouiX : nonX, y, 10);
    };
    markOui(formData['is-lamal'], 380.5, 451.5, 517);
    markOui(formData['is-laa'], 756.5, 827, 517);
    markOui(formData['is-pain-only'], 568.5, 639.5, 546);

    drawImgText(doc, formData['first-consultation'], 150, 615, 950, 11, 8);

    ['missing-teeth', 'defective-teeth', 'repaired-teeth', 'periodontal-teeth'].forEach((field) => {
      drawTeethOnGrid(doc, field);
    });

    drawImgTextMultiColumn(doc, formData['prosthetic-description'], 150, 910, 950, 11, 8, {
      maxRowsPerCol: 3,
      byEntries: true,
    });
    const prophylaxis = [formData.hygiene, formData.habits, formData.motivation].filter(Boolean).join(' | ');
    drawImgTextMultiColumn(doc, prophylaxis, 150, 990, 950, 11, 8, {
      maxRowsPerCol: 3,
    });
    drawImgTextMultiColumn(doc, formData.diagnostic, 150, 1075, 950, 11, 8, {
      maxRowsPerCol: 5,
    });
    drawImgTextMultiColumn(doc, formData['emergency-treatment'], 150, 1195, 950, 11, 8, {
      maxRowsPerCol: 3,
      byEntries: true,
    });
    drawImgTextMultiColumn(doc, formData['priority2-treatment'], 150, 1260, 950, 11, 8, {
      maxRowsPerCol: 14,
      byEntries: true,
    });

    // Background PNG may contain a baked-in date — clear then rewrite.
    doc.setFillColor(255, 255, 255);
    doc.rect(248 * sx, 1440 * sy, (560 - 248) * sx, (1462 - 1440) * sy, 'F');
    if (formData['place-date']) {
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text(String(formData['place-date']), 255 * sx, 1454 * sy);
    }

    if (signatureDataUrl) {
      try {
        doc.addImage(signatureDataUrl, signatureType, 820 * sx, 1460 * sy, 120 * sx, 40 * sy);
      } catch (e) {
        // ignore invalid signature
      }
    }

    // Page 2
    doc.addPage();
    doc.addImage(bg2, 'PNG', 0, 0, pageWidth, pageHeight);

    // TODO: §5.3 schéma dentaire — green marks on occlusal teeth (positions need recalibration)
    // parseTeethList(formData['dental-schema-teeth']).forEach((tooth) => {
    //   const pos = schemaToothPositions[tooth];
    //   if (pos) {
    //     drawSchemaToothMark(doc, pos[0], pos[1], 5);
    //   }
    // });

    drawImgText(doc, formData.comments, 150, 700, 950, 12, 8);

    doc.save('medident-neuchatel-form.pdf');
  };

  const OFFICE_FIELD_KEYS = OFFICE_FIELDS.map(({ key }) => key);

  const ADMINISTRATIVE_FIELDS = [
    'patient-name',
    'patient-firstname',
    'patient-address',
    'patient-phone',
    'patient-email',
    'patient-birth',
    'patient-sex',
    'patient-avs',
    'patient-asylum-n',
    'patient-personnel-nr',
    'dentist-office',
    'dentist',
    'rcc-nr',
    'phone-nr',
    'employee-name',
  ];

  const CONTEXT_FIELDS = ['is-lamal', 'is-laa', 'is-pain-only', 'first-consultation'];

  const TEETH_FIELDS = [
    'missing-teeth',
    'defective-teeth',
    'repaired-teeth',
    'periodontal-teeth',
    'prosthetic-description',
  ];

  const PROPHYLAXIS_FIELDS = ['hygiene', 'habits', 'motivation'];

  const TREATMENT_FIELDS = [
    'diagnostic',
    'emergency-treatment',
    'priority2-treatment',
    'dental-schema-teeth',
    'comments',
    'place-date',
  ];

  return (
    <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{t('consultation.steps.medidentNe')}</h2>
        <button
          type="button"
          onClick={handleGeneratePDF}
          style={{
            backgroundColor: colors.primary.selected,
            color: 'white',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            fontWeight: 500,
            border: 'none',
          }}
        >
          {t('insurance.buttons.generatePdf', 'Generate PDF')}
        </button>
      </div>

      {renderAccordionSection({
        title: t('medidentNe.sections.offices'),
        sectionFields: OFFICE_FIELD_KEYS,
        requireAtLeastOne: true,
        children: (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {OFFICE_FIELDS.map(({ key, labelKey }) => (
              <label key={key} className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!formData[key]}
                  onChange={(e) => handleChange(key, e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-[rgb(141,212,209)]"
                />
                {t(`medidentNe.fields.${labelKey}`)}
              </label>
            ))}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentNe.sections.administrative'),
        sectionFields: ADMINISTRATIVE_FIELDS,
        children: (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">{t('medidentNe.fields.patient')}</h3>
              {renderField('patient-name', t('medidentNe.fields.patientName'), 'text', true)}
              {renderField('patient-firstname', t('medidentNe.fields.patientFirstname'), 'text', true)}
              {renderField('patient-address', t('medidentNe.fields.patientAddress'), 'text', true)}
              {renderField('patient-phone', t('medidentNe.fields.patientPhone'), 'text', true)}
              {renderField('patient-email', t('medidentNe.fields.patientEmail'), 'text', true)}
              {renderField('patient-birth', t('medidentNe.fields.patientBirth'), 'date', true)}
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  {t('medidentNe.fields.patientSex')}
                  <span className="text-red-500">*</span>
                </span>
                <div className="flex gap-4">
                  {['m', 'f'].map((sex) => (
                    <label key={sex} className="inline-flex items-center gap-1 text-sm cursor-pointer">
                      <input
                        type="radio"
                        name="patient-sex"
                        checked={formData['patient-sex'] === sex}
                        onChange={() => handleChange('patient-sex', sex)}
                        className="h-4 w-4 border-gray-300"
                      />
                      {sex}
                    </label>
                  ))}
                </div>
              </div>
              {renderField('patient-avs', t('medidentNe.fields.patientAvs'), 'text', true)}
              {renderField('patient-asylum-n', t('medidentNe.fields.patientAsylumN'), 'text', true)}
              {renderField('patient-personnel-nr', t('medidentNe.fields.patientPersonnelNr'), 'text', true)}
            </div>
            <div className="space-y-3">
              <h3 className="font-medium text-gray-900">{t('medidentNe.fields.cabinet')}</h3>
              {renderField('dentist-office', t('medidentNe.fields.dentistOffice'), 'textarea', true)}
              {renderField('dentist', t('medidentNe.fields.dentist'), 'text', true)}
              {renderField('rcc-nr', t('medidentNe.fields.rccNr'), 'text', true)}
              {renderField('phone-nr', t('medidentNe.fields.phoneNr'), 'text', true)}
              {renderField('employee-name', t('medidentNe.fields.employeeName'), 'text', true)}
            </div>
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentNe.sections.context'),
        sectionFields: CONTEXT_FIELDS,
        children: (
          <div className="space-y-4">
            {renderOuiNon('is-lamal', t('medidentNe.fields.isLamal'), true)}
            {renderOuiNon('is-laa', t('medidentNe.fields.isLaa'), true)}
            {renderOuiNon('is-pain-only', t('medidentNe.fields.isPainOnly'), true)}
            {renderField('first-consultation', t('medidentNe.fields.firstConsultation'), 'textarea', true)}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentNe.sections.teeth'),
        sectionFields: TEETH_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-6">
            {[
              ['missing-teeth', 'missingTeeth'],
              ['defective-teeth', 'defectiveTeeth'],
              ['repaired-teeth', 'repairedTeeth'],
              ['periodontal-teeth', 'periodontalTeeth'],
            ].map(([field, labelKey]) => (
              <div key={field}>
                <p className="text-sm font-medium text-gray-700 mb-2">{t(`medidentNe.fields.${labelKey}`)}</p>
                <TeethSelector
                  selectedTeeth={formData[field] || ''}
                  onTeethSelect={(value) => handleChange(field, value)}
                />
              </div>
            ))}
            {renderField('prosthetic-description', t('medidentNe.fields.prostheticDescription'), 'textarea')}
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentNe.sections.prophylaxis'),
        sectionFields: PROPHYLAXIS_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">{t('medidentNe.fields.hygiene')}</label>
              <select
                value={formData.hygiene || ''}
                onChange={(e) => handleChange('hygiene', e.target.value)}
                className="rounded-md border-gray-300 shadow-sm"
              >
                <option value="">—</option>
                {HYGIENE_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            {renderField('habits', t('medidentNe.fields.habits'), 'textarea')}
            <div className="flex flex-col space-y-1">
              <label className="text-sm font-medium text-gray-700">{t('medidentNe.fields.motivation')}</label>
              <select
                value={formData.motivation || ''}
                onChange={(e) => handleChange('motivation', e.target.value)}
                className="rounded-md border-gray-300 shadow-sm"
              >
                <option value="">—</option>
                {MOTIVATION_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
        ),
      })}

      {renderAccordionSection({
        title: t('medidentNe.sections.treatment'),
        sectionFields: TREATMENT_FIELDS,
        isOptional: true,
        children: (
          <div className="space-y-4">
            {renderField('diagnostic', t('medidentNe.fields.diagnostic'), 'textarea')}
            {renderField('emergency-treatment', t('medidentNe.fields.emergencyTreatment'), 'textarea')}
            {renderField('priority2-treatment', t('medidentNe.fields.priority2Treatment'), 'textarea')}
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{t('medidentNe.fields.dentalSchema')}</p>
              <TeethSelector
                selectedTeeth={formData['dental-schema-teeth'] || ''}
                onTeethSelect={(value) => handleChange('dental-schema-teeth', value)}
              />
            </div>
            {renderField('comments', t('medidentNe.fields.comments'), 'textarea')}
            {renderField('place-date', t('medidentNe.fields.placeDate'))}
          </div>
        ),
      })}
    </form>
  );
};

export default MedidentNeForm;
